/**
 * A saved id is either a prompt group (a random rule inside it) or one or
 * more rules from that group. Rules of the same group share one row.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { TreeGroupLike } from '@/features/campaign-texts/utils/group-tree.utils';
import { getRandomizeScopeFolderOptions } from '@/features/campaign-texts/utils/group-tree.utils';

export interface PromptPick {
  folderId: string;
  /** Empty = a random rule inside the group. */
  ruleIds: string[];
}

function folderIds(groups: TreeGroupLike[]): Set<string> {
  return new Set(getRandomizeScopeFolderOptions(groups).map((f) => f.id));
}

function folderOf(id: string, groups: TreeGroupLike[], folders: Set<string>): string {
  const byId = new Map(groups.map((g) => [g.id, g]));
  let cur = byId.get(id);
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    if (cur.parent_id && folders.has(cur.parent_id)) return cur.parent_id;
    if (folders.has(cur.id)) return cur.id;
    cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
  }
  return id;
}

export function picksFromIds(ids: string[], groups: TreeGroupLike[]): PromptPick[] {
  const folders = folderIds(groups);
  const order: string[] = [];
  const byFolder = new Map<string, string[]>();
  for (const id of ids) {
    const folderId = folders.has(id) ? id : folderOf(id, groups, folders);
    if (!byFolder.has(folderId)) {
      order.push(folderId);
      byFolder.set(folderId, []);
    }
    const rules = byFolder.get(folderId);
    if (rules && id !== folderId) rules.push(id);
  }
  return order.map((folderId) => ({ folderId, ruleIds: byFolder.get(folderId) ?? [] }));
}

export function idsFromPicks(picks: PromptPick[]): string[] {
  const out: string[] = [];
  for (const pick of picks) {
    const ids = pick.ruleIds.length > 0 ? pick.ruleIds : [pick.folderId];
    for (const id of ids) if (id && !out.includes(id)) out.push(id);
  }
  return out;
}
